/**
 * PublishGate — Admin Phase 1d · read-only catalogue publish-gate drawer (C10).
 *
 * Founder ruling 14 Jun 2026 PM later · post-C9 sign-off:
 *   "C10 stays read-only publish gate / validation drawer. Shows whether
 *    image is present or missing. Shows current image preview. Missing
 *    image as a blocker. founder/desk get an Open-in-editor deeplink.
 *    read_only does NOT see editor deeplink or source/supplier URLs.
 *    No fake publish action. No create/edit/delete yet."
 *
 * Reads:
 *   GET /api/os-admin/publish-gate
 *   GET /api/os-admin/publish-gate/{source}/{id}
 */
import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { BACKEND_URL } from "@/lib/backend-url";

const SOURCE_LABELS = {
  escape_signature: "Escape Signature",
  enjoy_signature: "Enjoy Signature",
  enjoy_overlay: "Enjoy Overlay",
};
const SOURCE_KEYS = ["escape_signature", "enjoy_signature", "enjoy_overlay"];

const STATE_LABELS = {
  ready: "Ready",
  blocked: "Blocked",
  live: "Live",
  archived: "Archived",
};
const STATE_KEYS = ["ready", "blocked", "live", "archived"];

const FIELD_LABELS = {
  title: "Title",
  destination: "Destination",
  country: "Country",
  category: "Category",
  kind: "Kind",
  city: "City",
  concierge_whisper: "Concierge whisper",
  safety_reviewed: "Safety reviewed",
  booking_rule: "Booking rule",
  human_confirmed: "Human confirmed",
  hero_image: "Hero image",
  source_url_or_supplier_url: "Source URL or supplier URL",
  hero_image_url_or_placeholder: "Hero image or placeholder",
};

function pillToneForState(s) {
  if (s === "live") return "os-p-done";
  if (s === "ready") return "os-p-new";
  if (s === "blocked") return "os-p-urgent";
  if (s === "archived") return "os-p-prog";
  return "os-p-new";
}

function fieldHuman(key) {
  return FIELD_LABELS[key] || key;
}

function relativeTime(iso) {
  if (!iso) return "—";
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch { return iso; }
}

function absoluteImageUrl(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  // /api/signature/photo/{id} or any other backend-relative path
  return `${BACKEND_URL}${url.startsWith("/") ? "" : "/"}${url}`;
}


// ============================================================
// Drawer (right-side overlay)
// ============================================================
function PublishGateDrawer({ inspect, role, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!inspect) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      setDetail(null);
      try {
        const [src, id] = inspect.split(":");
        const r = await api.get(`/os-admin/publish-gate/${src}/${id}`);
        if (cancelled) return;
        setDetail(r.data);
      } catch (e) {
        if (cancelled) return;
        setError(
          formatApiError(e?.response?.data?.detail) ||
            "We could not load this draft. Please retry."
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [inspect]);

  useEffect(() => {
    if (!inspect) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [inspect, onClose]);

  if (!inspect) return null;

  const r = detail?.row || {};
  const v = r.validation || {};
  const isReadOnly = role === "read_only";

  return (
    <div className="os-pgate-overlay" data-testid="os-publish-gate-drawer">
      <div className="os-pgate-backdrop" onClick={onClose} />
      <aside className="os-pgate-panel">
        <header className="os-pgate-panel-head">
          <button
            type="button"
            className="os-pgate-close"
            onClick={onClose}
            data-testid="os-publish-gate-drawer-close"
            aria-label="Close drawer"
          >×</button>
          <div>
            <div className="os-pgate-panel-source">
              {SOURCE_LABELS[r.source] || r.source || "—"}
            </div>
            <h2 className="os-pgate-panel-title">{r.title || "—"}</h2>
            {r.subtitle && <div className="os-pgate-panel-sub">{r.subtitle}</div>}
          </div>
        </header>

        {error && (
          <div className="os-fyi-msg os-fyi-msg-err" data-testid="os-publish-gate-drawer-error">
            {error}
          </div>
        )}
        {loading && (
          <div className="os-fyi-msg" data-testid="os-publish-gate-drawer-loading">
            Loading…
          </div>
        )}

        {!loading && !error && detail && (
          <div className="os-pgate-panel-body">
            {/* ── Image preview / missing blocker ───────────────── */}
            <section className="os-pgate-sec">
              <div className="os-pgate-sec-label">Image</div>
              {r.image_url_preview ? (
                <div className="os-pgate-img-wrap" data-testid="os-publish-gate-image-preview">
                  <img
                    src={absoluteImageUrl(r.image_url_preview)}
                    alt={r.title || "draft image"}
                    className="os-pgate-img"
                    loading="lazy"
                  />
                  <div className="os-pgate-img-meta">
                    {r.has_image ? "Image loaded" : "Image unavailable"}
                  </div>
                </div>
              ) : (
                <div className="os-pgate-img-missing" data-testid="os-publish-gate-image-missing">
                  <span className="os-pill os-p-urgent">Missing image</span>
                  <div className="os-pgate-img-missing-help">
                    This draft is blocked from publishing until a hero
                    image is set.
                  </div>
                </div>
              )}
            </section>

            {/* ── Validation block ───────────────────────────── */}
            <section className="os-pgate-sec">
              <div className="os-pgate-sec-label">Publish gate</div>
              <div className="os-pgate-vstate">
                <span className={`os-pill ${pillToneForState(r.publish_state)}`}>
                  {STATE_LABELS[r.publish_state] || r.publish_state}
                </span>
                <span className="os-pgate-vstate-text">
                  {v.ok_to_publish
                    ? "All required fields present."
                    : `${v.missing_count || 0} missing field${(v.missing_count || 0) === 1 ? "" : "s"}.`}
                </span>
              </div>
              {!v.ok_to_publish && (v.missing_fields || []).length > 0 && (
                <ul className="os-pgate-missing" data-testid="os-publish-gate-missing-list">
                  {(v.missing_fields || []).map((f) => (
                    <li key={f} data-testid={`os-publish-gate-missing-${f}`}>
                      <span className="os-pgate-missing-dot">●</span>
                      {fieldHuman(f)}
                    </li>
                  ))}
                </ul>
              )}
              {(v.warnings || []).length > 0 && (
                <div className="os-pgate-warnings" data-testid="os-publish-gate-warnings">
                  <div className="os-pgate-sec-sub">Advisories</div>
                  {(v.warnings || []).map((w) => (
                    <span key={w} className="os-pill os-p-prog">{w}</span>
                  ))}
                </div>
              )}
            </section>

            {/* ── Draft snapshot ──────────────────────────────── */}
            <section className="os-pgate-sec">
              <div className="os-pgate-sec-label">Draft snapshot</div>
              <dl className="os-pgate-meta">
                {r.category && <><dt>Category</dt><dd>{r.category}</dd></>}
                {r.kind && <><dt>Kind</dt><dd>{r.kind}</dd></>}
                {(r.city || r.destination) && <><dt>Location</dt><dd>{r.city || r.destination}{r.country ? ` · ${r.country}` : ""}</dd></>}
                {r.provider_name && <><dt>Provider</dt><dd>{r.provider_name}</dd></>}
                {r.safety_level && <><dt>Safety level</dt><dd>{r.safety_level}</dd></>}
                {r.booking_rule && <><dt>Booking rule</dt><dd>{r.booking_rule}</dd></>}
                {r.concierge_whisper && <><dt>Concierge whisper</dt><dd className="os-pgate-meta-long">{r.concierge_whisper}</dd></>}
              </dl>
            </section>

            {/* ── Status history ─────────────────────────────── */}
            <section className="os-pgate-sec">
              <div className="os-pgate-sec-label">Status history</div>
              <dl className="os-pgate-meta">
                <dt>Created</dt>
                <dd title={r.created_at}>{relativeTime(r.created_at)}</dd>
                <dt>Updated</dt>
                <dd title={r.updated_at}>{relativeTime(r.updated_at)}</dd>
                {r.last_published_at && <>
                  <dt>Last published</dt>
                  <dd title={r.last_published_at}>{relativeTime(r.last_published_at)}</dd>
                </>}
              </dl>
            </section>

            {/* ── Editor deeplink (founder/desk only · read_only drops) ── */}
            {!isReadOnly && r.editor_deeplink && (
              <section className="os-pgate-sec">
                <a
                  href={r.editor_deeplink}
                  className="os-btn-brass"
                  data-testid="os-publish-gate-editor-deeplink"
                  target="_blank"
                  rel="noreferrer"
                >
                  Open in editor →
                </a>
                <div className="os-pgate-sec-sub">
                  Opens the legacy {SOURCE_LABELS[r.source]} editor in a
                  new tab. C10 itself does not write.
                </div>
              </section>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}


// ============================================================
// Main list page
// ============================================================
export default function PublishGate() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({
    total: 0, page: 1, per_page: 25,
    role: "read_only", has_more: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    source: [],
    publish_state: [],
    human_confirmed: null,
    q: "",
    page: 1,
    per_page: 25,
  });
  const [inspect, setInspect] = useState(() => {
    // Initialise from URL ?inspect=src:id
    if (typeof window === "undefined") return null;
    const p = new URLSearchParams(window.location.search);
    return p.get("inspect") || null;
  });

  // Sync URL when inspect changes (without full page reload)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (inspect) url.searchParams.set("inspect", inspect);
    else url.searchParams.delete("inspect");
    window.history.replaceState({}, "", url.toString());
  }, [inspect]);

  const buildQS = (f) => {
    const p = new URLSearchParams();
    (f.source || []).forEach((s) => p.append("source", s));
    (f.publish_state || []).forEach((s) => p.append("publish_state", s));
    if (f.human_confirmed === true) p.set("human_confirmed", "true");
    if (f.human_confirmed === false) p.set("human_confirmed", "false");
    if ((f.q || "").trim()) p.set("q", f.q.trim());
    p.set("page", String(f.page));
    p.set("per_page", String(f.per_page));
    return p.toString();
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const qs = buildQS(filters);
        const r = await api.get(`/os-admin/publish-gate?${qs}`);
        if (cancelled) return;
        setRows(r.data.rows || []);
        setMeta({
          total: r.data.total || 0,
          page: r.data.page || 1,
          per_page: r.data.per_page || 25,
          role: r.data.role || "read_only",
          has_more: !!r.data.has_more,
        });
      } catch (e) {
        if (cancelled) return;
        const friendly =
          formatApiError(e?.response?.data?.detail) ||
          "We could not load the publish gate just now. Please retry.";
        setError(friendly);
        setRows([]);
        setMeta((m) => ({ ...m, total: 0, has_more: false }));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [filters]);

  const toggleSet = (key, value) => {
    setFilters((prev) => {
      const cur = new Set(prev[key] || []);
      cur.has(value) ? cur.delete(value) : cur.add(value);
      return { ...prev, [key]: Array.from(cur), page: 1 };
    });
  };

  return (
    <div className="os-fyi-queue" data-testid="os-publish-gate">
      <div className="os-pagehead">
        <h1>Publish Gate</h1>
        <span className="count">
          {loading ? "Loading…" : `${meta.total} rows`}
        </span>
      </div>
      <div className="os-pagesub">
        Read-only catalogue validation drawer · <code>lc_escape_signature</code> · <code>lc_signature_drafts</code> · <code>lc_events_overlays</code> · role: <strong data-testid="os-publish-gate-role">{meta.role}</strong> · server-side redaction applied
      </div>

      <div className="os-fyi-filterblock" data-testid="os-publish-gate-filters">
        <div className="os-toolbar">
          <span className="os-fyi-label">Source</span>
          {SOURCE_KEYS.map((k) => (
            <button
              key={k}
              type="button"
              className={`os-fchip ${filters.source.includes(k) ? "on" : ""}`}
              onClick={() => toggleSet("source", k)}
              data-testid={`os-publish-gate-filter-source-${k}`}
            >
              {SOURCE_LABELS[k]}
            </button>
          ))}
        </div>

        <div className="os-toolbar">
          <span className="os-fyi-label">State</span>
          {STATE_KEYS.map((s) => (
            <button
              key={s}
              type="button"
              className={`os-fchip ${filters.publish_state.includes(s) ? "on" : ""}`}
              onClick={() => toggleSet("publish_state", s)}
              data-testid={`os-publish-gate-filter-state-${s}`}
            >
              {STATE_LABELS[s]}
            </button>
          ))}

          <span className="os-fyi-label os-fyi-label-spaced">Human confirmed</span>
          {[
            { key: "any", value: null, label: "any" },
            { key: "yes", value: true, label: "yes" },
            { key: "no", value: false, label: "no" },
          ].map((o) => (
            <button
              key={o.key}
              type="button"
              className={`os-fchip ${filters.human_confirmed === o.value ? "on" : ""}`}
              onClick={() => setFilters((p) => ({ ...p, human_confirmed: o.value, page: 1 }))}
              data-testid={`os-publish-gate-filter-confirmed-${o.key}`}
            >
              {o.label}
            </button>
          ))}

          <span className="os-spacer" />
          <input
            type="search"
            placeholder="Search title · destination · city · category"
            value={filters.q}
            onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value, page: 1 }))}
            maxLength={80}
            className="os-fyi-search"
            data-testid="os-publish-gate-search-input"
          />
        </div>
      </div>

      {error ? (
        <div className="os-fyi-msg os-fyi-msg-err" data-testid="os-publish-gate-error">
          {error}
        </div>
      ) : rows.length === 0 && !loading ? (
        <div className="os-fyi-msg" data-testid="os-publish-gate-empty">
          No drafts match these filters.
        </div>
      ) : (
        <table className="os-tbl" data-testid="os-publish-gate-table">
          <thead>
            <tr>
              <th>Source</th>
              <th>Title</th>
              <th>State</th>
              <th>Image</th>
              <th>Missing</th>
              <th>Confirmed</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const v = r.validation || {};
              const rowKey = `${r.source}:${r._id || r.id}`;
              return (
                <tr
                  key={rowKey}
                  onClick={() => setInspect(rowKey)}
                  className="os-pgate-row"
                  data-testid={`os-publish-gate-row-${r.source}-${r._id || r.id}`}
                  style={{ cursor: "pointer" }}
                >
                  <td>
                    <span className="os-surf">{SOURCE_LABELS[r.source] || r.source}</span>
                  </td>
                  <td>
                    <div className="os-fyi-title">{r.title || "—"}</div>
                    {(r.subtitle || r.destination || r.city) && (
                      <div className="os-fyi-sub">
                        {r.subtitle || r.destination || r.city}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={`os-pill ${pillToneForState(r.publish_state)}`}>
                      {STATE_LABELS[r.publish_state] || r.publish_state}
                    </span>
                  </td>
                  <td>
                    {r.has_image ? (
                      <span className="os-pill os-p-done" data-testid={`os-publish-gate-img-ok-${r._id || r.id}`}>
                        ✓ Set
                      </span>
                    ) : (
                      <span className="os-pill os-p-urgent" data-testid={`os-publish-gate-img-missing-${r._id || r.id}`}>
                        Missing
                      </span>
                    )}
                  </td>
                  <td>
                    {v.missing_count > 0 ? (
                      <span className="os-pill os-p-urgent" data-testid={`os-publish-gate-missing-count-${r._id || r.id}`}>
                        {v.missing_count} missing
                      </span>
                    ) : (
                      <span className="os-fyi-dim">—</span>
                    )}
                  </td>
                  <td>
                    {r.human_confirmed ? (
                      <span className="os-pill os-p-done">✓</span>
                    ) : (
                      <span className="os-fyi-dim">—</span>
                    )}
                  </td>
                  <td title={r.updated_at}>{relativeTime(r.updated_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="os-fyi-pagi" data-testid="os-publish-gate-pagination">
        <button
          type="button"
          className="os-btn-ghost"
          disabled={meta.page <= 1 || loading}
          onClick={() => setFilters((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
          data-testid="os-publish-gate-page-prev"
        >‹ Prev</button>
        <span className="os-fyi-pagemark">
          Page {meta.page} · {meta.per_page}/page
        </span>
        <button
          type="button"
          className="os-btn-ghost"
          disabled={!meta.has_more || loading}
          onClick={() => setFilters((p) => ({ ...p, page: p.page + 1 }))}
          data-testid="os-publish-gate-page-next"
        >Next ›</button>
      </div>

      <PublishGateDrawer
        inspect={inspect}
        role={meta.role}
        onClose={() => setInspect(null)}
      />
    </div>
  );
}
